/**
 * URL Updater
 * Update URL parameters without page reload
 */

import { state, saveToLocalStorage } from '../core/state.js';
import { encodeTier } from './tier-encoding.js';
import { isEmbeddedMode } from '../modes/embedded.js';

// Set by initFromURL() (page load and back/forward). The first URL sync after it
// rewrites the current history entry instead of adding a new one. Otherwise Back
// returns to the landing URL, which re-inits and pushes again (back-button trap).
let replaceNextUpdate = false;
export function replaceNextURLUpdate() {
  replaceNextUpdate = true;
}

// Query keys that search owns. initFromURL() treats the URL as a search only if
// one of these is present, so a landing with just gclid/utm_*/fbclid still gets
// the saved search or the IP location.
export const SEARCH_PARAM_KEYS = [
  'q',
  'live',
  'sportSlug',
  'types',
  'skills',
  'gender',
  'brand',
  'brandOthers',
  'typeBrand',
  'age',
  'genderFilters',
  'sort',
  'l',
  'beginDates',
  'endDates',
];

/**
 * Update URL parameters without page reload
 * Syncs current state to URL query string.
 *
 * Starts from the CURRENT query string and only sets or removes the keys that
 * search owns. Everything else (gclid, gbraid, wbraid, gad_*, hsa_*, utm_*,
 * fbclid, msclkid, _hsenc, _hsmi, any future tracking param) stays untouched.
 * Rule for new filters: always write them through put(), never params.set()
 * inside an if, so that clearing a filter also removes it from the URL.
 * Add the key to SEARCH_PARAM_KEYS as well.
 */
export function updateURL() {
  if (isEmbeddedMode()) return; // Embedded mode: don't update the URL
  const params = new URLSearchParams(window.location.search);
  const put = (key, value) => {
    if (value) {
      params.set(key, value);
    } else {
      params.delete(key);
    }
  };

  put('q', state.zip);

  // Only include live parameter when explicitly set to true
  put('live', state.live === true ? '1' : '');

  put('sportSlug', state.sportSlug.join(','));
  put('types', state.types.join(','));
  put('skills', state.skills.join(','));
  put('gender', state.gender.join(','));

  if (state.brandPageMode && state.brandPageBrand) {
    // Brand page mode: encode visual checkbox states into URL so they survive reload/share
    put('brand', state.brandPageBrandSelected ? state.brandPageBrand : '');
    put('brandOthers', state.brandPageAllOthers ? '1' : '');
    put('typeBrand', state.brandPageBrand);
  } else {
    put('brand', state.brand.join(','));
    put('brandOthers', '');
    put('typeBrand', '');
  }

  put('age', state.age && state.age.trim() ? state.age : '');

  // Gender filter checkboxes
  const genderFilters = [];
  if (state.filterCoed) genderFilters.push('coed');
  if (state.filterMaleOnly) genderFilters.push('maleOnly');
  if (state.filterFemaleOnly) genderFilters.push('femaleOnly');
  put('genderFilters', genderFilters.join(','));

  put('sort', state.sort && state.sort !== 'distance' ? state.sort : '');

  // Hidden tier filter - encode value for URL parameter 'l'
  put(
    'l',
    (state.tier || [])
      .map((t) => encodeTier(t))
      .filter(Boolean)
      .join(',')
  );

  put('beginDates', state.beginDates && state.beginDates.trim() ? state.beginDates : '');
  put('endDates', state.endDates && state.endDates.trim() ? state.endDates : '');

  const newURL =
    window.location.pathname +
    (params.toString() ? '?' + params.toString() : '') +
    window.location.hash;

  // Only update URL if it has actually changed
  const currentURL = window.location.pathname + window.location.search + window.location.hash;
  const replace = replaceNextUpdate;
  replaceNextUpdate = false;
  if (currentURL !== newURL) {
    if (replace) {
      window.history.replaceState({}, '', newURL);
    } else {
      window.history.pushState({}, '', newURL);
    }
  }

  // Save state to localStorage when URL is updated
  saveToLocalStorage();
}

console.log('📦 URL updater module loaded');
